import * as React from "react";
import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { queryClient } from "@/lib/queryClient";

interface RealTimeUpdatesContextType {
  refreshLeagueData: (leagueId: number) => void;
  refreshSubmissionCounts: (leagueId: number, roundId: number) => void;
  refreshVoteCounts: (leagueId: number, roundId: number) => void;
  setPollingEnabled: (enabled: boolean) => void;
  isPollingEnabled: boolean;
}

const RealTimeUpdatesContext = createContext<RealTimeUpdatesContextType | null>(null);

export function RealTimeUpdatesProvider({ children }: { children: ReactNode }) {
  const [isPollingEnabled, setPollingEnabled] = useState(true);

  // Function to refresh league data
  const refreshLeagueData = (leagueId: number) => {
    queryClient.invalidateQueries({ queryKey: [`/api/leagues/${leagueId}`] });
    queryClient.invalidateQueries({ queryKey: [`/api/leagues/${leagueId}/rounds`] });
    queryClient.invalidateQueries({ queryKey: [`/api/leagues/${leagueId}/members`] });
  };

  // Function to refresh submission counts
  const refreshSubmissionCounts = (leagueId: number, roundId: number) => {
    queryClient.invalidateQueries({ queryKey: [`/api/leagues/${leagueId}/rounds/${roundId}/submissions/count`] });
    queryClient.invalidateQueries({ queryKey: [`/api/leagues/${leagueId}/rounds/${roundId}/submissions`] });
  };

  // Function to refresh vote counts
  const refreshVoteCounts = (leagueId: number, roundId: number) => {
    queryClient.invalidateQueries({ queryKey: [`/api/leagues/${leagueId}/rounds/${roundId}/votes/count`] });
    queryClient.invalidateQueries({ queryKey: [`/api/leagues/${leagueId}/rounds/${roundId}/votes`] });
  };

  return (
    <RealTimeUpdatesContext.Provider
      value={{
        refreshLeagueData,
        refreshSubmissionCounts,
        refreshVoteCounts,
        setPollingEnabled,
        isPollingEnabled,
      }}
    >
      {children}
    </RealTimeUpdatesContext.Provider>
  );
}

export function useRealTimeUpdates() {
  const context = useContext(RealTimeUpdatesContext);
  if (!context) {
    throw new Error("useRealTimeUpdates must be used within a RealTimeUpdatesProvider");
  }
  return context;
}